import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {CallLogStringUtil} from '@app/shared/utils/call-log-string-util';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {AbsenceEntry, Contact, EndUser, EndUserCallLogEntry} from '@gt-api';
import {AbstractContactModel, ContactType} from './abstract-contact.model';
import {CallLogEntryModel} from './call-log-entry.model';

export class ContactModel extends AbstractContactModel {
  backendModel: Contact;
  company: string;
  departmentId: number;
  address: string;
  city: string;
  zip: string;
  memberState = 0;
  absenceEntry: AbsenceEntry;
  absenceId: number;
  global = true;
  nameValues: {};
  ownerOrganization: string;

  static convertContactModel(contact: Contact): ContactModel {
    const name = CallLogStringUtil.getPrettifyName(contact.firstName, contact.lastName);
    const typeString = contact['@type'] as string;
    let statusIcon = 'person';

    // Detect contact type based on properties
    let contactType: ContactType;
    if (contact['@type'] === ContactType[ContactType.EndUser]) {
      const endUserCheck = contact as EndUser;
      if (!endUserCheck.inboundTriggering && !endUserCheck.outboundTriggering) {
        contactType = ContactType.ExternalEndUser;
      } else {
        contactType = ContactType.EndUser;
      }
    } else if (contact['@type'] === ContactType[ContactType.Contact]) {
      if (contact.shortNumber) {
        contactType = ContactType.InternalContact;
      } else {
        contactType = ContactType.Contact;
      }
    }

    if (ContactType[typeString] === ContactType.Contact) {
      statusIcon = 'person_outline';
    }

    const endUser = contact as EndUser;
    const memberState = Math.max(endUser.inboundState, endUser.outboundState);

    return BuilderFactory.createBuilder(ContactModel)
      .backendModel(contact)
      .type(contactType)
      .id(contact.id)
      .status('')
      .statusIcon(statusIcon)
      .name(name)
      .firstName(contact.firstName)
      .lastName(contact.lastName)
      .phoneNumber(contact.fullNumber)
      .absenceId(endUser.absenceId)
      .memberState(memberState)
      .oname(endUser.organization)
      .login(endUser.login)
      .email(contact.email)
      .department(contact.department)
      .departmentId(endUser.departmentId)
      .company(contact.company)
      .shortNumber(contact.shortNumber)
      .imageId(contact.imageId)
      .address(contact.address)
      .city(contact.city)
      .zip(contact.zip)
      .global(contact.global)
      .ownerOrganization(contact.ownerOrganization)
      .administrator(endUser.administrator)
      .blocked(endUser.blocked)
      .build();
  }

  static convertCallLogEntryModel(callLogEntry: CallLogEntryModel): ContactModel {
    const statusIcon = 'person';

    return BuilderFactory.createBuilder(ContactModel)
      .backendModel(undefined)
      .statusIcon(statusIcon)
      .name(callLogEntry.caller)
      .phoneNumber(callLogEntry.fullNumber)
      .company(callLogEntry.company)
      .build();
  }

  static convertEndUserCallLogEntry(entry: EndUserCallLogEntry): ContactModel {
    return ContactModel.convertCallLogEntryModel(CallLogEntryModel.convertEndUserCallLogEntry(entry));
  }

  static convertVCardModel(vCard: string, oldNum?: string): ContactModel {
    const vCardParsed = JSON.parse(vCard);
    let name = '';
    let tel = '';
    let address = '';
    let city = '';
    let zip = '';

    vCardParsed[1].forEach(entries => {
      if (entries[0] === 'n') {
        const nameStrArr: string[] = entries[3];
        if (!name || name.length === 0) {
          let nameStr = '';
          for (const nameStrEntry of nameStrArr) {
            nameStr = nameStrEntry.length ? nameStr + ' ' + nameStrEntry : nameStr;
          }
          name = nameStr.trim();
        }
      } else if (entries[0] === 'fn') {
        name = entries[3];
      } else if (entries[0] === 'tel') {
        tel = entries[3];
      } else if (entries[0] === 'adr') {
        address = entries[3][2];
        city = entries[3][3];
        zip = entries[3][5];
      }
    });

    return BuilderFactory.createBuilder(ContactModel)
      .type(ContactType.Contact)
      .id(-1)
      .status('')
      .statusIcon('person_outline')
      .name(name)
      .address(address)
      .city(city)
      .zip(zip)
      .phoneNumber(oldNum ? oldNum : GtPhoneNumberUtil.toBackend(tel))
      .build();
  }

  hasTrigging(): boolean {
    if (this.isEndUser()) {
      const enduser = this.backendModel as EndUser;
      return enduser && enduser.inboundTriggering && enduser.outboundTriggering;
    }
    return false;
  }

  getBackendModel(): Contact {
    const contact = this.backendModel as Contact;
    contact.id = this.id;
    contact.fullNumber = GtPhoneNumberUtil.toBackend(this.phoneNumber);
    contact.firstName = this.firstName;
    contact.lastName = this.lastName;
    contact.shortNumber = this.shortNumber;
    contact.address = this.address;
    contact.email = this.email;
    contact.department = this.department;
    contact.global = this.global;
    contact.ownerOrganization = this.ownerOrganization;
    if (this.isEndUser() || this.isExternalEnduser()) {
      const endUser = contact as EndUser;
      endUser.departmentId = this.departmentId;
      endUser.organization = this.oname;
      endUser.login = this.login;
      endUser.inboundTriggering = this.isEndUser();
      endUser.outboundTriggering = this.isEndUser();
      endUser.administrator = this.administrator;
    }
    contact.company = this.company;
    if (this.isContact()) {
      contact.description = this.status;
    }
    return contact;
  }
}
